/**
 * El manual del sistema, servido por el propio sistema.
 *
 * **El contenido no está acá: está en `public/manual/*.md`.** Se sirve estático y se trae con
 * `fetch`, así que treinta páginas de manual pesan **cero** en el bundle — que ya viene grande
 * y el build lo marca. Además queda versionado en git, que es lo que hace que `git diff`
 * conteste "qué cambió del producto" y que un manual no envejezca en silencio.
 *
 * **Dos profundidades, un solo documento.** Cada página abre con un bloque fijo —*En una línea*
 * y *Lo que hay que saber*— y sigue con el detalle. El interruptor Resumen/Completo colapsa lo
 * segundo. Mantener un resumen aparte garantiza que se desincronice; un interruptor sobre el
 * mismo texto, no.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { BookOpen, Search, Loader2, ChevronRight, X } from 'lucide-react';

const RAIZ = `${process.env.PUBLIC_URL || ''}/manual`;

/** El separador entre el bloque de resumen y el detalle. Es una convención del contenido. */
const CORTE = '## En detalle';

export default function ManualPage() {
  const [params, setParams] = useSearchParams();
  const paginaId = params.get('p') || '';

  const [indice, setIndice] = useState(null);
  const [texto, setTexto] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [soloResumen, setSoloResumen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [corpus, setCorpus] = useState(null);
  const cuerpoRef = useRef(null);

  const paginas = useMemo(
    () => (indice?.partes || []).flatMap((p) => p.paginas.map((x) => ({ ...x, parte: p.titulo }))),
    [indice],
  );
  const actual = paginas.find((p) => p.id === paginaId) || paginas[0];

  useEffect(() => {
    fetch(`${RAIZ}/indice.json`)
      .then((r) => { if (!r.ok) throw new Error('indice'); return r.json(); })
      .then(setIndice)
      .catch(() => setError('No se pudo cargar el índice del manual.'));
  }, []);

  useEffect(() => {
    if (!actual) return;
    let vivo = true;
    setCargando(true);
    setError('');
    fetch(`${RAIZ}/${actual.id}.md`)
      .then((r) => { if (!r.ok) throw new Error('404'); return r.text(); })
      .then((t) => { if (vivo) { setTexto(t); cuerpoRef.current?.scrollTo(0, 0); } })
      .catch(() => { if (vivo) setError(`No se encontró la página "${actual.id}".`); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [actual]);

  /**
   * El corpus del buscador se arma **una sola vez y sólo al buscar**: treinta `fetch` al abrir
   * la pantalla serían treinta pedidos para algo que la mayoría de las visitas no usa.
   */
  const asegurarCorpus = useCallback(async () => {
    if (corpus || !paginas.length) return;
    const textos = await Promise.all(
      paginas.map((p) =>
        fetch(`${RAIZ}/${p.id}.md`).then((r) => (r.ok ? r.text() : '')).catch(() => '')),
    );
    setCorpus(paginas.map((p, i) => ({ ...p, texto: textos[i] })));
  }, [corpus, paginas]);

  const resultados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (q.length < 3 || !corpus) return null;
    return corpus
      .map((p) => {
        const plano = p.texto.toLowerCase();
        const donde = plano.indexOf(q);
        if (donde < 0 && !p.titulo.toLowerCase().includes(q)) return null;
        // Un fragmento alrededor del hallazgo: una lista de títulos no dice si el resultado sirve.
        const desde = Math.max(0, donde - 70);
        return {
          ...p,
          fragmento: donde < 0 ? p.resumen : `…${p.texto.slice(desde, desde + 180).replace(/\s+/g, ' ')}…`,
        };
      })
      .filter(Boolean)
      .slice(0, 12);
  }, [busqueda, corpus]);

  const mostrado = useMemo(() => {
    if (!soloResumen) return texto;
    const corte = texto.indexOf(CORTE);
    return corte > 0 ? texto.slice(0, corte) : texto;
  }, [texto, soloResumen]);

  const tieneDetalle = texto.includes(CORTE);

  const ir = (id) => { setParams({ p: id }); setBusqueda(''); };

  if (error && !indice) {
    return <div className="p-8 text-sm text-brand-danger">{error}</div>;
  }
  if (!indice) {
    return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>;
  }

  return (
    <div className="flex h-full">
      {/* Menú lateral */}
      <aside className="w-72 shrink-0 border-r border-brand-border bg-white flex flex-col">
        <div className="px-4 py-3 border-b border-brand-border">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-700 flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5 text-brand-primary" /> {indice.titulo}
          </p>
          <p className="text-[10px] text-zinc-400 mt-1 leading-snug">{indice.bajada}</p>
        </div>

        <div className="px-3 py-2 border-b border-brand-border">
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-2.5 text-zinc-400" />
            <Input
              className="h-7 text-xs rounded-sm pl-7 pr-6"
              placeholder="Buscar en el manual…"
              value={busqueda}
              onFocus={asegurarCorpus}
              onChange={(e) => setBusqueda(e.target.value)}
              data-testid="manual-buscar"
            />
            {busqueda && (
              <button onClick={() => setBusqueda('')} className="absolute right-2 top-2 text-zinc-400 hover:text-zinc-600">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          {busqueda.trim().length > 0 && busqueda.trim().length < 3 && (
            <p className="text-[10px] text-zinc-400 mt-1">Escribí al menos tres letras.</p>
          )}
        </div>

        <nav className="flex-1 overflow-auto py-2">
          {resultados ? (
            <div className="px-3">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
                {resultados.length === 0 ? 'Sin resultados' : `${resultados.length} resultado${resultados.length === 1 ? '' : 's'}`}
              </p>
              {resultados.map((r) => (
                <button
                  key={r.id}
                  onClick={() => ir(r.id)}
                  className="block w-full text-left px-2 py-2 rounded-sm hover:bg-zinc-50 mb-1"
                >
                  <span className="text-[11px] font-medium text-[#09090B]">{r.titulo}</span>
                  <span className="block text-[10px] text-zinc-500 leading-snug mt-0.5">{r.fragmento}</span>
                </button>
              ))}
            </div>
          ) : (
            (indice.partes || []).map((parte) => (
              <div key={parte.id} className="mb-3">
                <p className="px-4 text-[10px] uppercase tracking-[0.1em] text-zinc-400 mb-1">{parte.titulo}</p>
                {parte.paginas.map((p) => {
                  const activa = actual?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => ir(p.id)}
                      title={p.resumen}
                      data-testid={`manual-ir-${p.id}`}
                      className={`w-full text-left px-4 py-1.5 text-[11.5px] flex items-center gap-1 ${
                        activa ? 'bg-brand-primary/5 text-brand-primary font-medium border-l-2 border-brand-primary'
                               : 'text-zinc-600 hover:bg-zinc-50 border-l-2 border-transparent'}`}
                    >
                      {activa && <ChevronRight className="w-3 h-3 shrink-0" />}
                      <span className="truncate">{p.titulo}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </nav>

        {indice.nota_de_alcance && (
          /* Decir qué falta es parte del manual: un índice que no avisa que está incompleto
             hace que el lector concluya que lo que no encuentra no existe. */
          <p className="px-4 py-3 border-t border-brand-border text-[10px] text-zinc-500 leading-snug">
            {indice.nota_de_alcance}
          </p>
        )}
      </aside>

      {/* Cuerpo */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="h-12 shrink-0 border-b border-brand-border bg-white flex items-center justify-between px-6">
          <p className="text-xs text-zinc-400">
            {actual?.parte} <span className="text-zinc-300 mx-1">/</span>
            <span className="text-zinc-700 font-medium">{actual?.titulo}</span>
          </p>
          {tieneDetalle && (
            <div className="flex border border-brand-border rounded-sm overflow-hidden">
              {[['Resumen', true], ['Completo', false]].map(([label, valor]) => (
                <button
                  key={label}
                  onClick={() => setSoloResumen(valor)}
                  data-testid={`manual-nivel-${valor ? 'resumen' : 'completo'}`}
                  className={`text-[11px] px-3 h-7 ${soloResumen === valor ? 'bg-brand-primary text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div ref={cuerpoRef} className="flex-1 overflow-auto">
          {cargando ? (
            <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
          ) : error ? (
            <Card className="m-6 border border-brand-border rounded-sm p-6">
              <p className="text-sm text-zinc-700">{error}</p>
              <p className="text-xs text-zinc-400 mt-1">
                El índice la declara pero el archivo no está. Es un error del manual, no de tu navegador.
              </p>
            </Card>
          ) : (
            <article className="manual-cuerpo max-w-[46rem] mx-auto px-8 py-8 text-[13.5px] leading-relaxed text-zinc-700">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: (p) => <h1 className="text-xl font-semibold text-[#09090B] mt-0 mb-3" {...p} />,
                  h2: (p) => <h2 className="text-base font-semibold text-[#09090B] mt-8 mb-2 pb-1 border-b border-brand-border" {...p} />,
                  h3: (p) => <h3 className="text-sm font-semibold text-[#09090B] mt-5 mb-1.5" {...p} />,
                  p: (p) => <p className="mb-3" {...p} />,
                  ul: (p) => <ul className="list-disc pl-5 mb-3 space-y-1" {...p} />,
                  ol: (p) => <ol className="list-decimal pl-5 mb-3 space-y-1" {...p} />,
                  blockquote: (p) => <blockquote className="border-l-2 border-brand-primary pl-3 my-3 text-zinc-600 italic" {...p} />,
                  code: ({ inline, ...p }) => inline
                    ? <code className="bg-zinc-100 text-[12px] px-1 py-0.5 rounded-sm font-mono text-zinc-800" {...p} />
                    : <code className="block bg-zinc-50 border border-brand-border text-[11.5px] p-3 rounded-sm font-mono overflow-auto my-3" {...p} />,
                  table: (p) => <div className="overflow-auto my-4"><table className="w-full text-[12px] border border-brand-border" {...p} /></div>,
                  th: (p) => <th className="text-left bg-zinc-50 border-b border-brand-border px-2 py-1.5 font-semibold text-zinc-700" {...p} />,
                  td: (p) => <td className="border-b border-zinc-100 px-2 py-1.5 align-top" {...p} />,
                  img: ({ src, alt, ...p }) => (
                    // El markdown referencia `capturas/x.png`, relativo a donde vive el
                    // archivo. El navegador lo resolveria contra la URL de la pantalla
                    // —`/manual?p=…`— y buscaria `/capturas/x.png`, que no existe. Se ancla
                    // a la raiz del manual.
                    <figure className="my-5">
                      <img
                        className="border border-brand-border rounded-sm w-full"
                        src={src?.startsWith('http') ? src : `${RAIZ}/${src}`}
                        alt={alt || ''}
                        loading="lazy"
                        {...p}
                      />
                      {alt && <figcaption className="text-[11px] text-zinc-500 mt-1.5">{alt}</figcaption>}
                    </figure>
                  ),
                  a: (p) => {
                    // Un link interno del manual navega adentro del visor en vez de recargar.
                    const interno = (p.href || '').startsWith('?p=');
                    return interno
                      ? <button onClick={() => ir(p.href.slice(3))} className="text-brand-primary underline underline-offset-2">{p.children}</button>
                      : <a className="text-brand-primary underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />;
                  },
                }}
              >
                {mostrado}
              </ReactMarkdown>

              {soloResumen && tieneDetalle && (
                <Button
                  variant="outline" size="sm"
                  onClick={() => setSoloResumen(false)}
                  className="mt-4 h-7 text-xs rounded-sm"
                >
                  Ver el detalle
                </Button>
              )}
            </article>
          )}
        </div>
      </div>
    </div>
  );
}
